import type {
    ClientContractor,
    ContractorDirectoryPage,
    ContractorDirectoryQuery,
    ContractorDirectoryResponse,
    ContractorIdsResponse,
    ContractorOptionsPage,
    ContractorOptionsResponse,
} from '@numeo/types-core'
import { ContractorDirectoryTab, MAX_CONTRACTOR_DIRECTORY_BATCH_SIZE, MAX_CONTRACTOR_DIRECTORY_FAVORITES } from '@numeo/types-core'
import axios from 'axios'
import { getApiBaseUrlWithoutV1 } from '@/setup/SetupAxios'

const contractorPath = (boxTruckProfiles: boolean): string =>
    boxTruckProfiles ? '/box-truck-loads/profiles/contractors/v1' : '/application/directory/contractors/v1'

const contractorOptionsPath = (boxTruckProfiles: boolean): string =>
    boxTruckProfiles ? '/box-truck-loads/profiles/contractor-options/v1' : '/application/directory/contractor-options/v1'

const contractorIdsPath = (boxTruckProfiles: boolean): string =>
    boxTruckProfiles ? '/box-truck-loads/profiles/contractor-ids/v1' : '/application/directory/contractor-ids/v1'

const directoryConfig = () => ({ baseURL: getApiBaseUrlWithoutV1() })

const CONTRACTOR_BATCH_CONCURRENCY = 4

export const fetchContractorDirectoryPage = async (query: ContractorDirectoryQuery, boxTruckProfiles = false): Promise<ContractorDirectoryPage> => {
    const response = await axios.get<ContractorDirectoryResponse>(contractorPath(boxTruckProfiles), {
        ...directoryConfig(),
        params: {
            page: query.page,
            limit: query.limit,
            search: query.search || undefined,
            tab: query.tab,
            sortColumn: query.sortColumn,
            sortDirection: query.sortDirection,
            favoriteIds: query.favoriteIds.length > 0 ? query.favoriteIds.slice(0, MAX_CONTRACTOR_DIRECTORY_FAVORITES).join(',') : undefined,
            contractorId: query.contractorId,
            includeSummary: query.includeSummary,
            includeTotal: query.includeTotal,
        },
    })
    return response.data.data
}

/** Select every matching ID in one bounded server request. */
export const fetchMatchingContractorIds = async (query: ContractorDirectoryQuery, boxTruckProfiles = false): Promise<string[]> => {
    const response = await axios.get<ContractorIdsResponse>(contractorIdsPath(boxTruckProfiles), {
        ...directoryConfig(),
        params: {
            search: query.search || undefined,
            tab: query.tab,
            favoriteIds: query.favoriteIds.length > 0 ? query.favoriteIds.slice(0, MAX_CONTRACTOR_DIRECTORY_FAVORITES).join(',') : undefined,
        },
    })
    return response.data.data.ids
}

export const fetchContractorOptions = async (
    { search = '', page = 1, limit = 50, contractorIds = [] }: { search?: string; page?: number; limit?: number; contractorIds?: string[] } = {},
    boxTruckProfiles = false
): Promise<ContractorOptionsPage> => {
    const response = await axios.get<ContractorOptionsResponse>(contractorOptionsPath(boxTruckProfiles), {
        ...directoryConfig(),
        params: {
            page,
            limit: Math.min(limit, MAX_CONTRACTOR_DIRECTORY_BATCH_SIZE),
            search: search || undefined,
            contractorIds: contractorIds.length > 0 ? contractorIds.slice(0, MAX_CONTRACTOR_DIRECTORY_BATCH_SIZE).join(',') : undefined,
        },
    })
    return response.data.data
}

export const fetchContractorsByIds = async (contractorIds: string[], boxTruckProfiles = false): Promise<ClientContractor[]> => {
    const uniqueIds = [...new Set(contractorIds.filter(Boolean))]
    if (uniqueIds.length === 0) return []

    const chunks = Array.from({ length: Math.ceil(uniqueIds.length / MAX_CONTRACTOR_DIRECTORY_BATCH_SIZE) }, (_, index) =>
        uniqueIds.slice(index * MAX_CONTRACTOR_DIRECTORY_BATCH_SIZE, (index + 1) * MAX_CONTRACTOR_DIRECTORY_BATCH_SIZE)
    )
    const contractors: ClientContractor[] = []
    for (let index = 0; index < chunks.length; index += CONTRACTOR_BATCH_CONCURRENCY) {
        const batch = chunks.slice(index, index + CONTRACTOR_BATCH_CONCURRENCY)
        const pages = await Promise.all(
            batch.map(async (ids) => {
                const response = await axios.get<ContractorDirectoryResponse>(contractorPath(boxTruckProfiles), {
                    ...directoryConfig(),
                    params: {
                        page: 1,
                        limit: ids.length,
                        contractorIds: ids.join(','),
                        includeSummary: false,
                        includeTotal: false,
                        tab: ContractorDirectoryTab.ALL,
                    },
                })
                return response.data.data.items
            })
        )
        for (const page of pages) contractors.push(...page)
    }
    return contractors
}

export const fetchContractorById = async (contractorId: string, boxTruckProfiles = false): Promise<ClientContractor | undefined> => {
    return (await fetchContractorsByIds([contractorId], boxTruckProfiles))[0]
}
